import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import DeleteIcon from '@mui/icons-material/Delete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  taskTemplateInputSchema,
  type TaskTemplateDto,
  type TaskTemplateInput,
} from '@lawyer/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import type { z } from 'zod'
import { api } from '../../lib/api'
import { formResolver } from '../../lib/form'
import { qk } from '../../lib/queryClient'
import { ErrorAlert } from '../common/Feedback'

type FormValues = z.input<typeof taskTemplateInputSchema>

const EMPTY_ITEM = { name: '', description: '', dueOffsetDays: '' }

const EMPTY: FormValues = { name: '', description: '', items: [EMPTY_ITEM] }

type Props = {
  open: boolean
  template?: TaskTemplateDto | null
  onClose: () => void
}

export function TemplateEditorDialog({ open, template, onClose }: Props) {
  const queryClient = useQueryClient()
  const isEdit = Boolean(template)

  const form = useForm<FormValues, unknown, TaskTemplateInput>({
    resolver: formResolver(taskTemplateInputSchema),
    defaultValues: EMPTY,
  })

  const items = useFieldArray({ control: form.control, name: 'items' })

  useEffect(() => {
    if (!open) return
    form.reset(
      template
        ? {
            name: template.name,
            description: template.description ?? '',
            items: template.items.map((item) => ({
              name: item.name,
              description: item.description ?? '',
              dueOffsetDays: item.dueOffsetDays === null ? '' : String(item.dueOffsetDays),
            })),
          }
        : EMPTY
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template?.id])

  const save = useMutation({
    mutationFn: (values: TaskTemplateInput) =>
      isEdit
        ? api.put<TaskTemplateDto>(`/task-templates/${template?.id}`, values)
        : api.post<TaskTemplateDto>('/task-templates', values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.templates })
      onClose()
    },
  })

  const { errors } = form.formState

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
        <DialogTitle>{isEdit ? 'עריכת תבנית' : 'תבנית חדשה'}</DialogTitle>

        <DialogContent dividers>
          {save.error ? <ErrorAlert error={save.error} /> : null}

          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="שם התבנית"
              required
              autoFocus
              {...form.register('name')}
              error={Boolean(errors.name)}
              helperText={errors.name?.message ?? 'לדוגמה: פתיחת תיק מכירה'}
            />
            <TextField label="תיאור" multiline minRows={2} {...form.register('description')} />

            <Typography variant="subtitle2">פריטי התבנית</Typography>

            {items.fields.map((field, index) => (
              <Paper key={field.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Typography variant="body2" color="text.secondary" sx={{ pt: 1.5, minWidth: 20 }}>
                    {index + 1}
                  </Typography>
                  <Stack spacing={1} sx={{ flexGrow: 1 }}>
                    <TextField
                      label="שם הפריט"
                      required
                      {...form.register(`items.${index}.name`)}
                      error={Boolean(errors.items?.[index]?.name)}
                      helperText={errors.items?.[index]?.name?.message}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        label="תיאור"
                        size="small"
                        sx={{ flex: 3 }}
                        {...form.register(`items.${index}.description`)}
                      />
                      <TextField
                        label="יעד: ימים מההתחלה"
                        size="small"
                        type="number"
                        sx={{ flex: 1, minWidth: 150 }}
                        slotProps={{ htmlInput: { min: 0 } }}
                        {...form.register(`items.${index}.dueOffsetDays`)}
                        error={Boolean(errors.items?.[index]?.dueOffsetDays)}
                        helperText={errors.items?.[index]?.dueOffsetDays?.message}
                      />
                    </Stack>
                  </Stack>
                  <Stack>
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      onClick={() => items.move(index, index - 1)}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === items.fields.length - 1}
                      onClick={() => items.move(index, index + 1)}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={items.fields.length === 1}
                      onClick={() => items.remove(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Paper>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={() => items.append(EMPTY_ITEM)}
              sx={{ alignSelf: 'flex-start' }}
            >
              הוספת פריט
            </Button>

            <Typography variant="caption" color="text.secondary">
              "ימים מההתחלה" קובע את תאריך היעד של כל משימה ביחס לתאריך ההתחלה שנבחר בהפעלת התבנית
              (או להיום, אם לא נבחר). ריק = תאריך היעד הכללי. עריכת תבנית משפיעה רק על שימושים
              עתידיים ואינה משנה משימות שנוצרו ממנה בעבר.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>ביטול</Button>
          <Button type="submit" variant="contained" disabled={save.isPending}>
            {save.isPending ? 'שומר…' : 'שמירה'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
